import {AgentNumber, InvalidAgentNumber, isValidAgentNumber, ValidAgentNumber} from 'Agent/Accounts/models/AgentNumber';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export enum EnrollmentType {
  ORDINARY = 1,
  GROUP = 2,
  GENELCO_GROUP = 3,
}

export class InvalidEnrollmentApplication {
  public readonly key = 'InvalidEnrollmentApplication';

  constructor(
    public readonly accountNumber: string,
    public readonly enrollmentType: EnrollmentType | undefined,
    public readonly servicingAgentNumber: AgentNumber,
    public readonly file: File | undefined
  ) {}
}

export class ValidEnrollmentApplication {
  public static create(
    accountNumber: string,
    enrollmentType: EnrollmentType | undefined,
    servicingAgentNumber: string,
    file: File | undefined
  ): EnrollmentApplication {
    const validatedServicingAgentNumber = ValidAgentNumber.create(servicingAgentNumber);

    return enrollmentType && isValidAgentNumber(validatedServicingAgentNumber) && file
      ? new ValidEnrollmentApplication(accountNumber, enrollmentType, validatedServicingAgentNumber, file)
      : new InvalidEnrollmentApplication(accountNumber, enrollmentType, validatedServicingAgentNumber, file);
  }

  public readonly key = 'ValidEnrollmentApplication';
  private constructor(
    public readonly accountNumber: string,
    public readonly enrollmentType: EnrollmentType,
    public readonly servicingAgentNumber: ValidAgentNumber,
    public readonly file: File
  ) {}
}

export class SavingEnrollmentApplication {
  public readonly key = 'SavingEnrollmentApplication';

  constructor(
    public readonly accountNumber: string,
    public readonly enrollmentType: EnrollmentType,
    public readonly servicingAgentNumber: ValidAgentNumber,
    public readonly file: File
  ) {}
}

export class EnrollmentApplicationWithError {
  public readonly key = 'EnrollmentApplicationWithError';

  constructor(
    public readonly accountNumber: string,
    public readonly enrollmentType: EnrollmentType,
    public readonly servicingAgentNumber: ValidAgentNumber,
    public readonly file: File
  ) {}
}

export type EnrollmentApplication =
  | ValidEnrollmentApplication
  | InvalidEnrollmentApplication
  | EnrollmentApplicationWithError
  | SavingEnrollmentApplication;

export type EnrollmentApplicationOptions<TReturn> = ToMatchOptions<EnrollmentApplication, 'key', TReturn>;

export const matchEnrollmentApplication = <TReturn>(
  enrollmentApplication: EnrollmentApplication,
  options: EnrollmentApplicationOptions<TReturn>
): TReturn => {
  switch (enrollmentApplication.key) {
    case 'EnrollmentApplicationWithError':
      return options.EnrollmentApplicationWithError(enrollmentApplication);
    case 'InvalidEnrollmentApplication':
      return options.InvalidEnrollmentApplication(enrollmentApplication);
    case 'ValidEnrollmentApplication':
      return options.ValidEnrollmentApplication(enrollmentApplication);
    case 'SavingEnrollmentApplication':
      return options.SavingEnrollmentApplication(enrollmentApplication);
  }
};

export const isValidApplication = (application: EnrollmentApplication): application is ValidEnrollmentApplication =>
  application.key === 'ValidEnrollmentApplication';

export const EmptyEnrollmentApplication = new InvalidEnrollmentApplication(
  '',
  undefined,
  new InvalidAgentNumber(''),
  undefined
);
